import { cn } from '@mds/shared/lib/utils';

interface Props {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The visible text beside the track, which also labels the control. */
  label: string;
  disabled?: boolean;
  className?: string;
}

/**
 * An on/off control for a setting that takes effect on its own, where a
 * checkbox would read as one option among several. The label sits beside the
 * track and is part of the hit area.
 */
export default function Switch({ checked, onChange, label, disabled, className }: Props) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex min-h-(--target-control) items-center gap-3 rounded-xl text-base font-semibold text-ink transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:cursor-not-allowed disabled:opacity-55',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors duration-(--duration-fast) ease-standard',
          checked ? 'border-brand-edge bg-brand-edge' : 'border-ink/25 bg-raised',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-5.5 w-5.5 rounded-full bg-surface shadow-card transition-transform duration-(--duration-fast) ease-standard',
            checked && 'translate-x-5',
          )}
        />
      </span>
      {label}
    </button>
  );
}
